import { useNavigate } from "react-router-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import { AxiosError } from "axios";
import { PulseLoader } from "react-spinners";
import ThemeConstants from "../../../../config/theme/theme_constants";
import ErrorDialog from "../../../authentication/presentation/components/error_dialog";
import EyeOffIcon from "../../../../assets/images/eye_off_icon.svg?react";
import EyeIcon from "../../../../assets/images/eye_icon.svg?react";
import { useChangePassword } from "../../domain/hooks/use_change_password";
import { toast } from 'sonner';
import { useTranslation } from "../../../../i18n/useTranslation";

const ChangePasswordPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const theme = ThemeConstants();

    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirm, setConfirm] = useState('');

    const [showOldPassword, setShowOldPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const [oldPasswordError, setOldPasswordError] = useState('');
    const [newPasswordError, setNewPasswordError] = useState('');
    const [confirmError, setConfirmError] = useState('');

    const errorDivRef = useRef<HTMLDivElement>(null);

    const { mutate: changePassword, isPending } = useChangePassword();

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (errorDivRef.current && !errorDivRef.current.contains(event.target as Node)) {
                setOldPasswordError('');
                setNewPasswordError('');
                setConfirmError('');
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleSubmit = useCallback((e?: React.FormEvent) => {
        e?.preventDefault();
        if (isPending) return;

        if (!oldPassword) {
            setOldPasswordError(t('profile.password.current_required'));
        } else if (!newPassword) {
            setNewPasswordError(t('profile.password.new_required'));
        } else if (!newPassword.match(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^\w]).{8,}$/)) {
            setNewPasswordError(t('common.error.password_rules'));
        } else if (newPassword === oldPassword) {
            setNewPasswordError(t('profile.password.must_differ'));
        } else if (newPassword !== confirm) {
            setConfirmError(t('common.error.password_mismatch'));
        } else {
            changePassword({ oldPassword, newPassword }, {
                onSuccess: () => {
                    toast.success(t('profile.password.success'));
                    navigate(-1);
                },
                onError: (error: AxiosError) => {
                    const data = error.response?.data as Record<string, unknown> | undefined;
                    const message = (data?.['message'] as string) || '';
                    if (error.response?.status === 401 || message.toLowerCase().includes('invalid') || message.toLowerCase().includes('incorrect') || message.toLowerCase().includes('wrong')) {
                        setOldPasswordError(t('profile.password.current_incorrect'));
                    } else {
                        toast.error(t('profile.password.error'));
                    }
                }
            });
        }
    }, [isPending, oldPassword, newPassword, confirm, changePassword, navigate, t]);

    return (
        <div className="min-h-dvh flex flex-col items-center pt-[7.4dvh] pb-[3.7dvh] px-5 w-full justify-between gap-4">
            <div className="w-full relative flex justify-center items-center">
                <button
                    className='absolute left-0 cursor-pointer'
                    onClick={() => navigate(-1)}
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <mask id="mask0_change_pwd" maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
                            <rect width="24" height="24" fill="white"/>
                        </mask>
                        <g mask="url(#mask0_change_pwd)">
                            <path
                                d="M6.8 12.9998L9.7 15.8998C9.88334 16.0831 9.975 16.3165 9.975 16.5998C9.975 16.8831 9.88334 17.1165 9.7 17.2998C9.51667 17.4831 9.28334 17.5748 9 17.5748C8.71667 17.5748 8.48334 17.4831 8.3 17.2998L3.7 12.6998C3.6 12.5998 3.52917 12.4915 3.4875 12.3748C3.44584 12.2581 3.425 12.1331 3.425 11.9998C3.425 11.8665 3.44584 11.7415 3.4875 11.6248C3.52917 11.5081 3.6 11.3998 3.7 11.2998L8.3 6.6998C8.48334 6.51647 8.71667 6.4248 9 6.4248C9.28334 6.4248 9.51667 6.51647 9.7 6.6998C9.88334 6.88314 9.975 7.11647 9.975 7.3998C9.975 7.68314 9.88334 7.91647 9.7 8.0998L6.8 10.9998H20C20.2833 10.9998 20.5208 11.0956 20.7125 11.2873C20.9042 11.479 21 11.7165 21 11.9998C21 12.2831 20.9042 12.5206 20.7125 12.7123C20.5208 12.904 20.2833 12.9998 20 12.9998H6.8Z"
                                fill="black"
                            />
                        </g>
                    </svg>
                </button>
                <h1 className="text-lg font-bold">{t('profile.edit_password')}</h1>
            </div>

            <form className="flex flex-col w-full items-center max-w-105 gap-4" onSubmit={handleSubmit}>
                <div className='flex flex-col gap-1.25 input-container w-full'>
                    <label htmlFor="oldPassword" className='font-medium text-xs leading-5'>{t('profile.password.current')}</label>
                    <div className='relative'>
                        <input
                            type={showOldPassword ? "text" : "password"}
                            id="oldPassword"
                            name="oldPassword"
                            autoComplete="current-password"
                            className='bg-white rounded-full px-4 py-[10.5px] pr-12 focus:outline-none w-full'
                            value={oldPassword}
                            onChange={(e) => setOldPassword(e.target.value)}
                            disabled={isPending}
                        />
                        <button
                            type="button"
                            className='absolute right-4 top-1/2 transform -translate-y-1/2 cursor-pointer'
                            onClick={() => setShowOldPassword(!showOldPassword)}
                            disabled={isPending}
                        >
                            {showOldPassword ? <EyeOffIcon width={20} height={20}/> : <EyeIcon width={20} height={20}/>}
                        </button>
                    </div>
                    {oldPasswordError && (<ErrorDialog message={oldPasswordError} ref={errorDivRef}/>)}
                </div>

                <div className='flex flex-col gap-1.25 input-container w-full'>
                    <label htmlFor="newPassword" className='font-medium text-xs leading-5'>{t('common.field.new_password')}</label>
                    <div className='relative'>
                        <input
                            type={showNewPassword ? "text" : "password"}
                            id="newPassword"
                            name="newPassword"
                            autoComplete="new-password"
                            className='bg-white rounded-full px-4 py-[10.5px] pr-12 focus:outline-none w-full'
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            disabled={isPending}
                        />
                        <button
                            type="button"
                            className='absolute right-4 top-1/2 transform -translate-y-1/2 cursor-pointer'
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            disabled={isPending}
                        >
                            {showNewPassword ? <EyeOffIcon width={20} height={20}/> : <EyeIcon width={20} height={20}/>}
                        </button>
                    </div>
                    {newPasswordError && (<ErrorDialog message={newPasswordError} ref={errorDivRef}/>)}
                </div>

                <div className='flex flex-col gap-1.25 input-container w-full'>
                    <label htmlFor="confirmPassword" className='font-medium text-xs leading-5'>{t('profile.password.confirm_new')}</label>
                    <div className='relative'>
                        <input
                            type={showConfirmPassword ? "text" : "password"}
                            id="confirmPassword"
                            name="confirmPassword"
                            autoComplete="new-password"
                            className='bg-white rounded-full px-4 py-[10.5px] pr-12 focus:outline-none w-full'
                            value={confirm}
                            onChange={(e) => setConfirm(e.target.value)}
                            disabled={isPending}
                        />
                        <button
                            type="button"
                            className='absolute right-4 top-1/2 transform -translate-y-1/2 cursor-pointer'
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            disabled={isPending}
                        >
                            {showConfirmPassword ? <EyeOffIcon width={20} height={20}/> : <EyeIcon width={20} height={20}/>}
                        </button>
                    </div>
                    {confirmError && (<ErrorDialog message={confirmError} ref={errorDivRef}/>)}
                </div>
            </form>

            <button
                className={`rounded-full text-white font-bold px-9.25 py-2.25 mx-auto ${isPending ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                style={{ backgroundColor: theme.UvibesPink }}
                onClick={handleSubmit}
                disabled={isPending}
            >
                {isPending ? <PulseLoader speedMultiplier={0.75} size={8} color="white"/> : t('profile.edit_password')}
            </button>
        </div>
    );
};

export default ChangePasswordPage;
